import { type Editor } from '@tiptap/core';
import { ReactRenderer } from '@tiptap/react';
import { exitSuggestion, type SuggestionKeyDownProps, type SuggestionProps } from '@tiptap/suggestion';
import tippy, { type GetReferenceClientRect, type Placement, type Instance as TippyInstance } from 'tippy.js';

import {
  clearSlashAutoReopenSuppression,
  clearSyntheticSlashTrigger,
  closeSlashSuggestion,
  dismissSlashTrigger,
  forceRemoveStaleSlashMenuUi,
  getEditorViewSafe,
  hasPendingSlashTrigger,
  hasSyntheticSlashTrigger,
  isEditorViewReady,
  isSlashAutoReopenSuppressed,
  isSlashDebugEnabled,
  isSlashTriggerDismissed,
  markSlashMenuOpened,
  replaySlashTokenAtCursor,
  slashCommandsPluginKey,
} from './slashCore';
import { SlashMenu, type SlashMenuHandle, type SlashMenuProps, SlashSearchPill } from './slashMenuView';
import type { SlashItem } from './slashTypes';

type SlashSuggestionProps = SuggestionProps<SlashItem, SlashItem>;

const hasSlashQuery = (query: string) => query.trim().length > 0;
const shouldHideEmptySlashMenu = (props: SlashSuggestionProps) => !props.items.length && !hasSlashQuery(props.query);
const slashPopupZIndex = 100100;
const searchPillLeftInset = 2;
const searchPillTopOffset = -8;
const searchPillGapWhenMenuOnTop = 5;
const popupLeftOffset = 8;
const slashMenuPreferredHeight = 360;
const slashMenuDefaultMaxHeight = 420;
const slashMenuVerticalPadding = 12;
const slashMenuDecisionGap = 8;
const slashMenuBottomOffset = 12;
const slashMenuTopOffset = Math.abs(searchPillTopOffset) + searchPillGapWhenMenuOnTop;
const slashMenuTopFallbackBias = 24;

const safeDestroy = (instance: { destroy: () => void } | null | undefined) => {
  if (!instance) return;
  try {
    instance.destroy();
  } catch (error) {
    if (error instanceof DOMException && error.name === 'NotFoundError') {
      return;
    }
    throw error;
  }
};

const readClientRect = (props: SlashSuggestionProps) => {
  try {
    return props.clientRect?.() ?? null;
  } catch {
    return null;
  }
};

const getClientRectGetter = (
  props: SlashSuggestionProps,
  getLastKnownRect: () => DOMRect,
  setLastKnownRect: (rect: DOMRect) => void,
): GetReferenceClientRect => {
  return () => {
    const rect = readClientRect(props);
    if (rect) {
      setLastKnownRect(rect);
      return rect;
    }
    if (isEditorViewReady(props.editor)) {
      try {
        const coords = props.editor.view.coordsAtPos(props.range.from);
        const coordsRect = new DOMRect(
          coords.left,
          coords.top,
          Math.max(1, coords.right - coords.left),
          Math.max(1, coords.bottom - coords.top),
        );
        setLastKnownRect(coordsRect);
        return coordsRect;
      } catch {
        const view = getEditorViewSafe(props.editor);
        const dom = view?.dom as HTMLElement | null | undefined;
        if (dom && dom.isConnected) {
          const editorRect = dom.getBoundingClientRect();
          const fallbackRect = new DOMRect(
            editorRect.left,
            editorRect.top,
            Math.max(1, editorRect.width),
            Math.max(1, editorRect.height),
          );
          setLastKnownRect(fallbackRect);
          return fallbackRect;
        }
      }
    }
    return getLastKnownRect();
  };
};

const cloneRect = (rect: DOMRect) =>
  new DOMRect(rect.left, rect.top, Math.max(1, rect.width), Math.max(1, rect.height));

const getVerticalBounds = (editor: Editor) => {
  const visualViewport = window.visualViewport;
  const viewportTop = visualViewport?.offsetTop ?? 0;
  const viewportHeight = visualViewport?.height ?? (window.innerHeight || 0);
  const viewportBottom = viewportTop + viewportHeight;
  let topBoundary = viewportTop;
  let bottomBoundary = viewportBottom;

  const viewDom = getEditorViewSafe(editor)?.dom;
  if (viewDom instanceof HTMLElement) {
    const boundaryElement = viewDom.closest('[data-supertext-viewport-boundary]');
    if (boundaryElement instanceof HTMLElement) {
      const boundaryRect = boundaryElement.getBoundingClientRect();
      topBoundary = Math.max(viewportTop, Math.min(viewportBottom, boundaryRect.top));
      bottomBoundary = Math.max(topBoundary, Math.min(viewportBottom, boundaryRect.bottom));
    } else {
      const panelBody = viewDom.closest('#step-panel-layout-body');
      if (panelBody instanceof HTMLElement) {
        const panelBodyRect = panelBody.getBoundingClientRect();
        topBoundary = Math.max(viewportTop, Math.min(viewportBottom, panelBodyRect.top));

        const panelColumn = panelBody.parentElement;
        const bottomSource =
          panelColumn instanceof HTMLElement ? panelColumn.getBoundingClientRect().bottom : panelBodyRect.bottom;
        bottomBoundary = Math.max(topBoundary, Math.min(viewportBottom, bottomSource));
      }
    }
  }

  return { topBoundary, bottomBoundary };
};

const decidePlacement = (props: SlashSuggestionProps): Placement => {
  const rect = readClientRect(props);
  if (!rect) return 'bottom-start';
  const { topBoundary, bottomBoundary } = getVerticalBounds(props.editor);
  const availableBelow = Math.max(0, bottomBoundary - rect.bottom - slashMenuDecisionGap);
  const availableAbove = Math.max(0, rect.top - topBoundary - slashMenuDecisionGap);
  const fitsBelow = availableBelow >= slashMenuPreferredHeight;
  const fitsAbove = availableAbove >= slashMenuPreferredHeight;

  if (fitsBelow) return 'bottom-start';
  if (fitsAbove) return 'top-start';

  return availableAbove - availableBelow > slashMenuTopFallbackBias ? 'top-start' : 'bottom-start';
};

export const createSlashSuggestionRender = () => {
  let renderer: ReactRenderer<SlashMenuHandle, SlashMenuProps> | null = null;
  let searchRenderer: ReactRenderer<{ update: (query: string) => void }, { query: string }> | null = null;
  let popup: TippyInstance | null = null;
  let detachPositionListeners: (() => void) | null = null;
  let popperStyleObserver: MutationObserver | null = null;
  let isApplyingFrozenStyle = false;
  let latestProps: SlashSuggestionProps | null = null;
  let didSelect = false;
  let lastKnownRect = new DOMRect(0, 0, 1, 1);
  let frozenClientRect: DOMRect | null = null;
  let frozenPopupRect: DOMRect | null = null;
  let frozenWindowScroll = { left: 0, top: 0 };
  let trackedScrollElements: HTMLElement[] = [];
  const trackedScrollPositions = new Map<HTMLElement, { left: number; top: number }>();
  let openedAt = 0;
  let autoReopenAttempts = 0;
  const debug = (scope: string, payload: Record<string, unknown>) => {
    if (!isSlashDebugEnabled()) return;
    console.debug(`[slash-ab][supertext] ${scope}`, payload);
  };
  const getClientRect = (props: SlashSuggestionProps): GetReferenceClientRect =>
    getClientRectGetter(
      props,
      () => lastKnownRect,
      rect => {
        lastKnownRect = rect;
      },
    );
  const getFrozenClientRect: GetReferenceClientRect = () => frozenClientRect ?? lastKnownRect;

  const getAccumulatedScrollDelta = () => {
    let deltaX = window.scrollX - frozenWindowScroll.left;
    let deltaY = window.scrollY - frozenWindowScroll.top;

    trackedScrollElements.forEach(element => {
      const initial = trackedScrollPositions.get(element);
      if (!initial) return;
      deltaX += element.scrollLeft - initial.left;
      deltaY += element.scrollTop - initial.top;
    });

    return { deltaX, deltaY };
  };

  const resolveViewportPosition = (rect: DOMRect) => {
    const { deltaX, deltaY } = getAccumulatedScrollDelta();
    return {
      left: rect.left - deltaX,
      top: rect.top - deltaY,
    };
  };

  const resolveReferenceRect = (props: SlashSuggestionProps) => {
    const rect = frozenClientRect ?? readClientRect(props);
    if (!rect) return null;

    const position = resolveViewportPosition(rect);
    return new DOMRect(position.left, position.top, Math.max(1, rect.width), Math.max(1, rect.height));
  };

  const applyMenuSize = (props: SlashSuggestionProps, placement?: Placement | string) => {
    const element = renderer?.element as HTMLElement | null;
    if (!element) return;

    const rect = resolveReferenceRect(props);
    if (!rect) {
      element.style.removeProperty('--slash-menu-max-height');
      element.style.removeProperty('--slash-menu-scroll-max-height');
      return;
    }

    const { topBoundary, bottomBoundary } = getVerticalBounds(props.editor);
    const activePlacement = placement ?? popup?.props.placement ?? 'bottom-start';
    const availableHeight = activePlacement.toString().startsWith('top')
      ? rect.top - topBoundary - slashMenuTopOffset
      : bottomBoundary - rect.bottom - slashMenuBottomOffset;
    const maxHeight = Math.max(0, Math.min(slashMenuDefaultMaxHeight, Math.floor(availableHeight)));
    const scrollMaxHeight = Math.max(0, maxHeight - slashMenuVerticalPadding);

    element.style.setProperty('--slash-menu-max-height', `${maxHeight}px`);
    element.style.setProperty('--slash-menu-scroll-max-height', `${scrollMaxHeight}px`);
  };

  const updateSearchPillPosition = (props: SlashSuggestionProps) => {
    const element = searchRenderer?.element as HTMLElement | null;
    if (!element) return;

    const rect = frozenClientRect ?? readClientRect(props);
    if (!rect) return;
    const position = resolveViewportPosition(rect);
    element.style.left = `${Math.round(position.left + searchPillLeftInset)}px`;
    element.style.top = `${Math.round(position.top + searchPillTopOffset)}px`;
  };

  const applyFrozenPopupPosition = () => {
    const popperElement = popup?.popper as HTMLElement | undefined;
    if (!popperElement || !popperElement.isConnected) return;

    const currentRect = popperElement.getBoundingClientRect();
    if (!frozenPopupRect) {
      frozenPopupRect = new DOMRect(currentRect.left, currentRect.top, currentRect.width, currentRect.height);
    } else {
      frozenPopupRect = new DOMRect(frozenPopupRect.left, frozenPopupRect.top, currentRect.width, currentRect.height);
    }

    const position = resolveViewportPosition(frozenPopupRect);
    const placement =
      popperElement.querySelector('.tippy-box')?.getAttribute('data-placement') ?? popup?.props.placement ?? '';
    let top = position.top;
    if (placement.startsWith('top')) {
      const referenceRect = frozenClientRect ?? (latestProps ? readClientRect(latestProps) : null);
      if (referenceRect) {
        const referencePosition = resolveViewportPosition(referenceRect);
        const searchTop = referencePosition.top + searchPillTopOffset;
        top = searchTop - searchPillGapWhenMenuOnTop - frozenPopupRect.height;
      }
    }

    isApplyingFrozenStyle = true;
    popperElement.style.position = 'fixed';
    popperElement.style.left = `${Math.round(position.left + popupLeftOffset)}px`;
    popperElement.style.top = `${Math.round(top)}px`;
    popperElement.style.margin = '0';
    popperElement.style.transform = 'none';
    isApplyingFrozenStyle = false;
  };

  const collectScrollableAncestors = (props: SlashSuggestionProps): HTMLElement[] => {
    const targets: HTMLElement[] = [];
    const viewDom = getEditorViewSafe(props.editor)?.dom;
    if (!(viewDom instanceof HTMLElement)) return targets;

    let current: HTMLElement | null = viewDom.parentElement;
    while (current) {
      const style = window.getComputedStyle(current);
      const isScrollableY =
        (style.overflowY === 'auto' || style.overflowY === 'scroll' || style.overflowY === 'overlay') &&
        current.scrollHeight > current.clientHeight + 1;
      const isScrollableX =
        (style.overflowX === 'auto' || style.overflowX === 'scroll' || style.overflowX === 'overlay') &&
        current.scrollWidth > current.clientWidth + 1;
      if (isScrollableY || isScrollableX) {
        targets.push(current);
      }
      current = current.parentElement;
    }

    return targets;
  };

  const captureFrozenScrollState = (props: SlashSuggestionProps) => {
    trackedScrollElements = collectScrollableAncestors(props);
    trackedScrollPositions.clear();
    trackedScrollElements.forEach(element => {
      trackedScrollPositions.set(element, {
        left: element.scrollLeft,
        top: element.scrollTop,
      });
    });
    frozenWindowScroll = {
      left: window.scrollX,
      top: window.scrollY,
    };
  };

  const attachPositionListeners = () => {
    const targets: EventTarget[] = [window, ...trackedScrollElements];
    const onMove = () => {
      if (latestProps) {
        applyMenuSize(latestProps);
      }
      applyFrozenPopupPosition();
      if (latestProps) {
        updateSearchPillPosition(latestProps);
      }
    };

    targets.forEach(target => {
      target.addEventListener('scroll', onMove as EventListener, { capture: true, passive: true });
    });
    window.addEventListener('resize', onMove, { capture: true, passive: true });

    return () => {
      targets.forEach(target => {
        target.removeEventListener('scroll', onMove as EventListener, { capture: true });
      });
      window.removeEventListener('resize', onMove, { capture: true });
    };
  };

  const attachPopperStyleObserver = () => {
    const popperElement = popup?.popper as HTMLElement | undefined;
    if (!popperElement || !popperElement.isConnected) return () => {};

    const observer = new MutationObserver(() => {
      if (isApplyingFrozenStyle) return;
      applyFrozenPopupPosition();
    });
    observer.observe(popperElement, {
      attributes: true,
      attributeFilter: ['style'],
    });
    popperStyleObserver = observer;

    return () => {
      observer.disconnect();
      if (popperStyleObserver === observer) {
        popperStyleObserver = null;
      }
    };
  };

  const closeMenu = (fallbackEditor?: Editor, suppressAutoReopen = false) => {
    const activeEditor = latestProps?.editor ?? fallbackEditor;
    if (activeEditor) {
      closeSlashSuggestion(activeEditor, suppressAutoReopen);
      return;
    }
    popup?.hide();
    forceRemoveStaleSlashMenuUi();
  };

  const buildSlashMenuProps = (props: SlashSuggestionProps): SlashMenuProps => ({
    items: props.items,
    query: props.query,
    command: (item: SlashItem) => {
      didSelect = true;
      props.command(item);
      closeMenu(props.editor, false);
    },
  });

  return {
    onStart: (props: SlashSuggestionProps) => {
      if (shouldHideEmptySlashMenu(props)) {
        closeSlashSuggestion(props.editor, true);
        forceRemoveStaleSlashMenuUi();
        return;
      }

      const editorElementOption = props.editor.options.element;
      const editorHostElement =
        editorElementOption instanceof HTMLElement
          ? editorElementOption
          : editorElementOption &&
              typeof editorElementOption === 'object' &&
              'mount' in editorElementOption &&
              editorElementOption.mount instanceof HTMLElement
            ? editorElementOption.mount
            : null;

      debug('slashSuggestion:onStart', {
        fieldName: editorHostElement?.closest('[id*="super-text-root"]')?.id ?? null,
        query: props.query,
      });

      latestProps = props;
      markSlashMenuOpened();
      autoReopenAttempts = 0;
      detachPositionListeners?.();
      detachPositionListeners = null;
      popperStyleObserver?.disconnect();
      popperStyleObserver = null;
      captureFrozenScrollState(props);
      const currentRect = getClientRect(props)();
      frozenClientRect = cloneRect(currentRect);
      lastKnownRect = frozenClientRect;
      frozenPopupRect = null;
      safeDestroy(searchRenderer);
      safeDestroy(popup);
      safeDestroy(renderer);
      searchRenderer = null;
      popup = null;
      renderer = null;
      forceRemoveStaleSlashMenuUi();

      searchRenderer = new ReactRenderer(SlashSearchPill, {
        editor: props.editor,
        props: { query: props.query },
      });

      if (searchRenderer.element) {
        const element = searchRenderer.element as HTMLElement;
        element.classList.add('slash-menu__search-floating');
        // The pill previews where the block will land inside the form, so it
        // follows the form theme. It's appended to <body> (outside any
        // [data-theme-scope]), so copy the scope's inline theme variables over.
        const themeScope = editorHostElement?.closest('[data-theme-scope]');
        const themeStyle = themeScope?.getAttribute('style');
        if (themeStyle) {
          element.style.cssText += `; ${themeStyle}`;
        }
        element.style.position = 'fixed';
        element.style.zIndex = `${slashPopupZIndex}`;
        element.style.pointerEvents = 'none';
        document.body.appendChild(element);
        updateSearchPillPosition(props);
      }

      renderer = new ReactRenderer(SlashMenu, {
        editor: props.editor,
        props: buildSlashMenuProps(props),
      });

      const initialPlacement: Placement = decidePlacement(props);
      applyMenuSize(props, initialPlacement);
      openedAt = Date.now();
      popup = tippy(document.body, {
        getReferenceClientRect: getFrozenClientRect,
        content: renderer.element,
        showOnCreate: true,
        interactive: true,
        popperOptions: {
          strategy: 'fixed',
          modifiers: [
            { name: 'eventListeners', options: { scroll: false, resize: false } },
            { name: 'flip', enabled: false },
            { name: 'preventOverflow', enabled: false },
            { name: 'computeStyles', options: { adaptive: false } },
          ],
        },
        appendTo: () => document.body,
        arrow: false,
        theme: 'slash-menu',
        zIndex: slashPopupZIndex,
        trigger: 'manual',
        placement: initialPlacement,
        offset: ({ placement }) => (placement.startsWith('top') ? [0, 6] : [0, 12]),
        onClickOutside: () => {
          debug('slashSuggestion:onClickOutside', { elapsed: Date.now() - openedAt });
          if (Date.now() - openedAt < 150) {
            return;
          }
          closeMenu(props.editor, true);
        },
      });

      detachPositionListeners = attachPositionListeners();
      const detachObserver = attachPopperStyleObserver();
      requestAnimationFrame(() => {
        applyFrozenPopupPosition();
        updateSearchPillPosition(props);
        requestAnimationFrame(() => {
          applyFrozenPopupPosition();
          updateSearchPillPosition(props);
        });
      });
      detachPositionListeners = (() => {
        const detachMoves = detachPositionListeners;
        return () => {
          detachMoves?.();
          detachObserver();
        };
      })();
      requestAnimationFrame(() => updateSearchPillPosition(props));
    },

    onUpdate: (props: SlashSuggestionProps) => {
      if (shouldHideEmptySlashMenu(props)) {
        closeMenu(props.editor, true);
        return;
      }

      latestProps = props;
      searchRenderer?.updateProps({ query: props.query });
      applyMenuSize(props);
      applyFrozenPopupPosition();
      updateSearchPillPosition(props);
      renderer?.updateProps(buildSlashMenuProps(props));
      requestAnimationFrame(() => {
        applyMenuSize(props);
        applyFrozenPopupPosition();
        updateSearchPillPosition(props);
      });
    },

    onKeyDown: (props: SuggestionKeyDownProps) => {
      if (props.event.key === 'Escape') {
        if (latestProps) {
          dismissSlashTrigger(latestProps.editor, latestProps.range.from);
          closeSlashSuggestion(latestProps.editor, true);
        } else {
          exitSuggestion(props.view, slashCommandsPluginKey);
        }
        return true;
      }
      return renderer?.ref?.onKeyDown(props.event) ?? false;
    },

    onExit: () => {
      debug('slashSuggestion:onExit', {
        hadLatestProps: Boolean(latestProps),
        didSelect,
      });

      const suppressAutoReopen = isSlashAutoReopenSuppressed();
      if (!didSelect && latestProps) {
        const { editor, range, query } = latestProps;
        const expected = `/${query}`;
        const actual = editor.state.doc.textBetween(range.from, range.to, '\n', '\n');
        const wasSyntheticSlash = hasSyntheticSlashTrigger(editor);
        const isDismissedSlash = isSlashTriggerDismissed(editor, range.from);
        const shouldRetryAutoOpen = query === '' && hasPendingSlashTrigger(editor) && !suppressAutoReopen;

        debug('slashSuggestion:onExit:state', {
          query,
          expected,
          actual,
          isFocused: editor.isFocused,
          hasPending: hasPendingSlashTrigger(editor),
          autoReopenAttempts,
          shouldRetryAutoOpen,
          suppressAutoReopen,
          wasSyntheticSlash,
        });

        if (shouldRetryAutoOpen && autoReopenAttempts < 3) {
          autoReopenAttempts += 1;
          const delay = Math.min(24 * autoReopenAttempts, 72);
          const replayAfterMount = (attempt = 0) => {
            debug('slashSuggestion:onExit:retryTick', {
              attempt,
              isViewReady: isEditorViewReady(editor),
              hasPending: hasPendingSlashTrigger(editor),
              isFocused: editor.isFocused,
            });

            if (!isEditorViewReady(editor)) {
              if (attempt < 8) {
                window.setTimeout(() => replayAfterMount(attempt + 1), 40);
              }
              return;
            }

            const slashState = slashCommandsPluginKey.getState(editor.state) as { active?: boolean } | null;
            if (slashState?.active) return;
            if (!hasPendingSlashTrigger(editor)) return;

            if (!editor.isFocused) {
              const view = getEditorViewSafe(editor);
              view?.focus();
              try {
                editor.commands.focus('end');
              } catch {
                return;
              }
            }

            const replayed = replaySlashTokenAtCursor(editor);
            debug('slashSuggestion:onExit:replay', {
              delay,
              attempt,
              autoReopenAttempts,
              replayed,
              isFocused: editor.isFocused,
            });

            if (!replayed && hasPendingSlashTrigger(editor) && attempt < 8) {
              window.setTimeout(() => replayAfterMount(attempt + 1), 40);
            }
          };

          window.setTimeout(() => replayAfterMount(0), delay);
        } else if ((wasSyntheticSlash || (suppressAutoReopen && !isDismissedSlash)) && actual === expected) {
          const view = getEditorViewSafe(editor);
          if (view) {
            view.dispatch(view.state.tr.delete(range.from, range.to));
          }
        }
      }

      safeDestroy(searchRenderer);
      safeDestroy(popup);
      safeDestroy(renderer);
      detachPositionListeners?.();
      detachPositionListeners = null;
      popperStyleObserver?.disconnect();
      popperStyleObserver = null;
      if (suppressAutoReopen) {
        clearSlashAutoReopenSuppression();
      }
      searchRenderer = null;
      popup = null;
      renderer = null;
      forceRemoveStaleSlashMenuUi();
      if (latestProps) {
        clearSyntheticSlashTrigger(latestProps.editor);
      }
      latestProps = null;
      didSelect = false;
      frozenClientRect = null;
      frozenPopupRect = null;
      frozenWindowScroll = { left: 0, top: 0 };
      trackedScrollElements = [];
      trackedScrollPositions.clear();
      openedAt = 0;
    },
  };
};
